var _class, _descriptor, _descriptor2, _descriptor3;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Service, { inject as service } from '@ember/service';
import { initDashboardQueryClient, QueryCache, MutationCache, matchQuery, initDashboardQueryContext } from '@squareup/dashboard-query';
import { __initSaasSharedUiQueryContext } from '@squareup/saas-shared-ui/api';
import { dashboardCurrentUser } from '@squareup/dashboard-query/queries/dashboardcurrentuser';
import * as Sentry from '@sentry/browser';
import { redirect, getUrlFlag } from '@square/dashboard-utilities/utils/window';
import { isTesting } from '@embroider/macros';
const DEFAULT_QUERY_RETRIES = 3;
const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 403, 404]);
const IGNORE_STATUS_CODES = new Set([401]);
const defaultQueryOptions = {
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  useErrorBoundary: false
};
function getErrorStatusCode(error) {
  if (error && typeof error === 'object' && 'cause' in error) {
    const errorObj = error;
    return getErrorStatusCode(errorObj.cause);
  }
  if (isConnectError(error)) {
    return error.rawResponse.status;
  } else if (isRpcError(error)) {
    return error.responseStatus;
  }
  return null;
}
function isConnectError(error) {
  if (!error || typeof error !== 'object') {
    return false;
  }
  return 'rawResponse' in error;
}
function isRpcError(error) {
  if (!error || typeof error !== 'object') {
    return false;
  }
  return 'responseStatus' in error;
}
let QueryClient = (_class = class QueryClient extends Service {
  constructor(owner) {
    super(owner);
    _initializerDefineProperty(this, "environment", _descriptor, this);
    _initializerDefineProperty(this, "employeeInfo", _descriptor2, this);
    _initializerDefineProperty(this, "userInfo", _descriptor3, this);
    _defineProperty(this, "queryClientInstance", void 0);
    const client = initDashboardQueryClient({
      defaultOptions: {
        queries: {
          ...defaultQueryOptions,
          retry: (count, error) => {
            if (this.isDestroyed || this.isDestroying) {
              return false;
            }
            if (this.environment.isTesting) {
              return false;
            }
            const statusCode = getErrorStatusCode(error);
            if (statusCode && NON_RETRYABLE_STATUS_CODES.has(statusCode)) {
              return false;
            }
            return count < DEFAULT_QUERY_RETRIES;
          }
        }
      },
      queryCache: new QueryCache({
        onError: (error, query) => {
          const statusCode = getErrorStatusCode(error);
          if (!statusCode || !IGNORE_STATUS_CODES.has(statusCode)) {
            const errorContext = query.meta?.errorContext || {};
            errorContext.query = {
              queryHash: query.queryHash
            };
            Sentry.captureException(error, errorContext);
          }
          if (statusCode === 401) {
            this.redirectToSignIn();
          }
        }
      }),
      mutationCache: new MutationCache({
        onSuccess: (_data, _variables, _context, mutation) => {
          return this.queryClientInstance.invalidateQueries({
            predicate: query => {
              return (Array.isArray(mutation.meta?.invalidates) && mutation.meta?.invalidates?.some(queryKey => matchQuery({
                queryKey
              }, query))) ?? true;
            }
          });
        },
        onError: (error, _variables, _context, mutation) => {
          const statusCode = getErrorStatusCode(error);
          if (!statusCode || !IGNORE_STATUS_CODES.has(statusCode)) {
            const errorContext = mutation.meta?.errorContext || {};
            Sentry.captureException(error, errorContext);
          }
          if (statusCode === 401) {
            this.redirectToSignIn();
          }
        }
      })
    });
    this.queryClientInstance = client;
  }
  initContext() {
    this.queryClientInstance.setQueryData(dashboardCurrentUser.get.queryKey, {
      user: this.userInfo.data,
      employee: this.employeeInfo.data
    });
    const logRpc = getUrlFlag('logrpc', location);
    const loggingEnabled = Boolean(!this.environment.isHeadless && logRpc !== false && (this.environment.isDevelopment || this.environment.isStaging || logRpc));
    initDashboardQueryContext({
      loggingEnabled,
      environment: this.environment.name
    });
    __initSaasSharedUiQueryContext({
      loggingEnabled,
      environment: this.environment.name
    });
  }

  /**
   * Registers the query client instances to the window as globals so that
   * they can be consumed by dashboard-web, which is server-side rendered
   * and cannot be passed props during mount.
   */
  registerGlobals() {
    // eslint-disable-next-line dashboard/use-utilities-window
    window.__dashboardQueryClientV5 = this.queryClientInstance;
    // eslint-disable-next-line dashboard/use-utilities-window
    window.__dashboardQueryClient = this.queryClientInstance;
  }
  get instance() {
    return this.queryClientInstance;
  }

  /** @deprecated - use instance() instead */
  get instanceV5() {
    return this.queryClientInstance;
  }
  redirectToSignIn() {
    if (!isTesting()) {
      redirect(`/login?return_to=${window.encodeURIComponent(window.location.pathname)}`);
    }
  }
  willDestroy() {
    // https://tanstack.com/query/latest/docs/reference/QueryClient/#queryclientclear
    // Clears all connected caches, necessary for cleanup on test teardown
    this.queryClientInstance.clear();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "environment", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "employeeInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { QueryClient as default };